import React, { useCallback, useId, useState } from "react";
import { Box, IconButton, Popover, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import type { DashboardProfileCompleteness } from "@services/facilityDashboard";
import {
  cmsNursingHomePdcTooltipAriaLabel,
  cmsNursingHomePdcTooltipHeading,
  cmsNursingHomePdcTooltipIntro,
  resolveCmsNursingHomePdcTypeLabels,
  type CmsNursingHomePdcMeta,
} from "@utils/cmsNursingHomePdc";
import { DASHBOARD } from "./dashboardTheme";

function DashboardInfoPopover({
  ariaLabel,
  children,
}: {
  ariaLabel: string;
  children: React.ReactNode;
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const titleId = useId();

  const handleOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  return (
    <>
      <IconButton
        component="span"
        size="small"
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? titleId : undefined}
        onClick={handleOpen}
        sx={{
          p: 0.25,
          ml: 0.25,
          color: DASHBOARD.accent,
          "&:hover": { bgcolor: "rgba(14, 116, 144, 0.08)" },
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 16 }} />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            id: titleId,
            role: "dialog",
            "aria-label": ariaLabel,
            sx: {
              maxWidth: 440,
              width: "min(440px, calc(100vw - 32px))",
              p: 0,
              borderRadius: 2,
              border: `1px solid ${DASHBOARD.borderSubtle}`,
              boxShadow: "0 12px 40px rgba(15, 23, 42, 0.14)",
            },
          },
        }}
      >
        <Box sx={{ position: "relative", px: 2, py: 1.75, pr: 5 }}>
          <IconButton
            size="small"
            aria-label="Close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              top: 6,
              right: 6,
              color: DASHBOARD.header,
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          {children}
        </Box>
      </Popover>
    </>
  );
}

function InfoSection({ heading, body }: { heading: string; body: React.ReactNode }) {
  return (
    <Box sx={{ mb: 1.25, "&:last-child": { mb: 0 } }}>
      <Typography sx={{ fontWeight: 700, fontSize: 13, color: DASHBOARD.header, mb: 0.5 }}>
        {heading}
      </Typography>
      {typeof body === "string" ? (
        <Typography sx={{ fontSize: 13, color: DASHBOARD.text, lineHeight: 1.45 }}>{body}</Typography>
      ) : (
        body
      )}
    </Box>
  );
}

function InfoList({ items }: { items: string[] }) {
  return (
    <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
      {items.map((item) => (
        <Typography
          key={item}
          component="li"
          sx={{ fontSize: 13, color: DASHBOARD.text, lineHeight: 1.45, mb: 0.35 }}
        >
          {item}
        </Typography>
      ))}
    </Box>
  );
}

export function buildFacilityProfileCompletenessTooltipTitle(
  pc: DashboardProfileCompleteness,
): React.ReactNode {
  const complete = pc.completeness_percentage >= 100;
  const requiredMissing = pc.missing_fields.filter(Boolean);
  const optionalMissing = (pc.missing_optional_fields ?? []).filter(Boolean);

  return (
    <>
      <Typography sx={{ fontWeight: 700, fontSize: 15, color: DASHBOARD.header, mb: 1.25, pr: 1 }}>
        Facility profile completeness
      </Typography>
      <InfoSection
        heading="What the numbers mean"
        body={
          <InfoList
            items={[
              `${pc.completed_count} / ${pc.total_count} is visible profile fields filled, not tabs.`,
              "The percentage weights required fields twice; optional fields still move the bar.",
            ]}
          />
        }
      />
      {complete ? (
        <InfoSection
          heading="You are all set"
          body="Required and optional visible fields are filled. Use Edit → to keep license dates and certifications current."
        />
      ) : (
        <>
          <InfoSection
            heading="What to do"
            body={
              <InfoList
                items={[
                  "Select Edit → to open Facility Profile.",
                  "Fill required fields (marked with * in the profile).",
                  "Add optional details (licenses, expirations, accreditation, capacity) to reach 100%.",
                  "Save each section; saved values count toward completeness.",
                ]}
              />
            }
          />
          {requiredMissing.length > 0 ? (
            <InfoSection heading="Required still needed" body={requiredMissing.join(", ")} />
          ) : null}
          {optionalMissing.length > 0 ? (
            <InfoSection heading="Optional still needed" body={optionalMissing.join(", ")} />
          ) : requiredMissing.length === 0 ? (
            <InfoSection
              heading='Why "Missing" may show —'
              body="Only required gaps are listed under Missing. Remaining empty optional fields are not listed but still lower the count and percentage."
            />
          ) : null}
        </>
      )}
    </>
  );
}

export function FacilityProfileCompletenessInfoTooltip({
  profileCompleteness,
}: {
  profileCompleteness: DashboardProfileCompleteness;
}) {
  return (
    <DashboardInfoPopover ariaLabel="How to complete your facility profile">
      {buildFacilityProfileCompletenessTooltipTitle(profileCompleteness)}
    </DashboardInfoPopover>
  );
}

export function RegulationImpactInfoTooltip({ lookbackDays }: { lookbackDays: number }) {
  return (
    <DashboardInfoPopover ariaLabel="How regulation impact works on the dashboard">
      <Typography sx={{ fontWeight: 700, fontSize: 15, color: DASHBOARD.header, mb: 1.25, pr: 1 }}>
        Regulation impact
      </Typography>
      <InfoSection
        heading="What this shows"
        body={`Regulations flagged in policy and procedure analyses from the last ${lookbackDays} days, with impact on your facility.`}
      />
      <InfoSection
        heading="When the list is empty"
        body="No regulation findings were recorded from recent document analyses in this period."
      />
      <InfoSection
        heading="What to do"
        body={
          <InfoList
            items={[
              "Select Discover → to browse regulations and reference codes for your facility type.",
              "Run Policy & Procedure or related analyses under Continuous Compliance to refresh findings.",
            ]}
          />
        }
      />
    </DashboardInfoPopover>
  );
}

export function PenaltyExposureInfoTooltip() {
  return (
    <DashboardInfoPopover ariaLabel="How penalty exposure is calculated">
      <Typography sx={{ fontWeight: 700, fontSize: 15, color: DASHBOARD.header, mb: 1.25, pr: 1 }}>
        Penalty exposure
      </Typography>
      <InfoSection
        heading="What this shows"
        body="Estimated dollar exposure for the current calendar quarter, based on your Potential Penalties tab."
      />
      <InfoSection
        heading="What is included"
        body={
          <InfoList
            items={[
              "Compliance Calendar: deadlines due this quarter (uses the higher end of a range when shown).",
              "Potential Penalties & Compliance Risks: worked-example amounts from the penalties table.",
              "Ongoing standing duties from the calendar that appear under Potential Penalties.",
            ]}
          />
        }
      />
      <InfoSection
        heading="Trend and chart"
        body="Quarter-over-quarter change and the sparkline appear only when the prior quarter had exposure to compare against. If there is no prior-quarter base, only the current-quarter total is shown."
      />
    </DashboardInfoPopover>
  );
}

export function ForwardEstimateHighConfidenceInfoTooltip() {
  return (
    <DashboardInfoPopover ariaLabel="What high-confidence forward estimate means">
      <Typography sx={{ fontWeight: 700, fontSize: 15, color: DASHBOARD.header, mb: 1.25, pr: 1 }}>
        High-confidence
      </Typography>
      <InfoSection
        heading="What this shows"
        body="The portion of the 12-month forward estimate grounded in observable CMS enforcement and quality data — 62% of each facility's projected total."
      />
      <InfoSection
        heading="How it is calculated"
        body={
          <InfoList
            items={[
              "Per facility, the model annualizes 36-month CMP and denied-payment history and compares it to quality risk score × certified beds.",
              "The higher value drives the base estimate; SFF or SFF-candidate status adds a 35% boost; each immediate-jeopardy citation adds $40K.",
              "High-confidence = 62% of that facility total, summed across the network.",
              "For Unicomply-managed facilities, the total may come from your facility penalty-exposure tile; the 62% / 38% split still applies.",
            ]}
          />
        }
      />
    </DashboardInfoPopover>
  );
}

export function ForwardEstimateModeledTailInfoTooltip() {
  return (
    <DashboardInfoPopover ariaLabel="What modeled tail forward estimate means">
      <Typography sx={{ fontWeight: 700, fontSize: 15, color: DASHBOARD.header, mb: 1.25, pr: 1 }}>
        Modeled tail
      </Typography>
      <InfoSection
        heading="What this shows"
        body="The statistical tail of projected penalty exposure — 38% of each facility's 12-month forward estimate, summed across the network."
      />
      <InfoSection
        heading="What it represents"
        body={
          <InfoList
            items={[
              "Less certain but still probable outcomes: future survey cycles, enforcement escalation, and trajectory shifts not fully captured in recent penalty history.",
              "Staffing, readmission, and VBP trends inform the overall forward model but appear here as the upside-risk portion beyond direct history.",
              "High-confidence plus modeled tail equals the total 12-month forward estimate.",
            ]}
          />
        }
      />
    </DashboardInfoPopover>
  );
}

export function ForwardEstimateDriving80InfoTooltip() {
  return (
    <DashboardInfoPopover ariaLabel="What driving 80 percent of forward estimate means">
      <Typography sx={{ fontWeight: 700, fontSize: 15, color: DASHBOARD.header, mb: 1.25, pr: 1 }}>
        Driving 80%
      </Typography>
      <InfoSection
        heading="What this shows"
        body="How many facilities account for at least 80% of the network's total 12-month forward estimate — a concentration (Pareto) metric, not a dollar amount."
      />
      <InfoSection
        heading="How it is calculated"
        body={
          <InfoList
            items={[
              "Facilities are ranked by individual 12-month forward estimate, highest first.",
              "Estimates are added until the running total reaches 80% or more of the network total.",
              "The count shown is how many facilities were needed to reach that threshold; the rest of the portfolio shares the remaining ~20%.",
            ]}
          />
        }
      />
    </DashboardInfoPopover>
  );
}

export function NetworkVbpImpactInfoTooltip() {
  return (
    <DashboardInfoPopover ariaLabel="How FY24 VBP impact is estimated">
      <Typography sx={{ fontWeight: 700, fontSize: 15, color: DASHBOARD.header, mb: 1.25, pr: 1 }}>
        FY24 VBP impact
      </Typography>
      <InfoSection
        heading="What this shows"
        body="Estimated network-wide Medicare SNF Value-Based Purchasing (VBP) dollar impact for the CMS performance year, summed across portfolio facilities."
      />
      <InfoSection
        heading="How it is calculated"
        body={
          <InfoList
            items={[
              "Per facility: (average daily census × 365 × 30% Medicare share × $500/day per diem) × (Incentive Payment Multiplier − 1.0).",
              "Average daily census comes from CMS Nursing Home Compare provider data for each facility.",
              "Incentive Payment Multiplier comes from the CMS SNF VBP facility performance file.",
              "Network total is the sum of per-facility estimates. Facilities with multiplier below 1.0 are counted in the subtitle.",
            ]}
          />
        }
      />
      <InfoSection
        heading="Note"
        body="CMS publishes multipliers but not facility-level dollar impact on Nursing Home Compare. This is a proxy estimate, not actual Medicare claims. When CMS dollar fields are present in imported data, those values are used instead."
      />
    </DashboardInfoPopover>
  );
}

export function DocumentExpiryInfoTooltip() {
  return (
    <DashboardInfoPopover ariaLabel="How to manage document expiry dates">
      <Typography sx={{ fontWeight: 700, fontSize: 15, color: DASHBOARD.header, mb: 1.25, pr: 1 }}>
        Document expiry
      </Typography>
      <InfoSection
        heading="What this shows"
        body="License, accreditation, and certification expiration dates from your facility profile, sorted by urgency."
      />
      <InfoSection
        heading="What to do"
        body={
          <InfoList
            items={[
              "Select Manage → to open Facility Profile.",
              "Enter expiration dates for licenses and certifications in the relevant sections.",
              "Renew documents before they expire; the dashboard highlights items within 90 days.",
            ]}
          />
        }
      />
    </DashboardInfoPopover>
  );
}

export function CmsNursingHomePdcInfoTooltip({
  meta,
}: {
  meta?: CmsNursingHomePdcMeta | null;
}) {
  const labels = resolveCmsNursingHomePdcTypeLabels(meta);
  return (
    <DashboardInfoPopover ariaLabel={cmsNursingHomePdcTooltipAriaLabel()}>
      <Typography sx={{ fontWeight: 700, fontSize: 15, color: DASHBOARD.header, mb: 1.25, pr: 1 }}>
        {cmsNursingHomePdcTooltipHeading()}
      </Typography>
      <InfoSection heading="Scope" body={cmsNursingHomePdcTooltipIntro()} />
      <InfoList items={labels} />
    </DashboardInfoPopover>
  );
}
